
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Loader2, FileText, Download, Save, Sparkles, BookOpen, Calendar, Clock, Upload, Link, Plus, X, Eye, Edit, Search, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, FileIcon, ImageIcon, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { geminiService } from '@/services/geminiService';
import { ocrService } from '@/services/ocrService';
import { motion } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import FeatureGuard from '@/components/trial/FeatureGuard';

interface LessonPlan {
  ID: number;
  teacher_id: number;
  class_id: number;
  title: string;
  subject: string;
  grade_level: string;
  duration: number;
  content: string;
  objectives: string;
}

interface ResourceSummary {
  type: 'pdf' | 'image' | 'weblink';
  fileName: string;
  summary: string;
  keyPoints: string[];
  extractedText: string;
  url?: string;
}

const LessonPlanGenerator: React.FC = () => {
  const { user, canAccessFeature } = useAuth();
  const { toast } = useToast();
  const [classes, setClasses] = useState<any[]>([]);
  const [savedPlans, setSavedPlans] = useState<LessonPlan[]>([]);
  const [formData, setFormData] = useState({
    subject: '',
    topic: '',
    grade: '',
    duration: '',
    objectives: '',
    additionalNotes: '',
    classId: '',
    durationDays: 1
  });
  const [resources, setResources] = useState({
    pdfs: [] as File[],
    screenshots: [] as File[],
    webLinks: ['']
  });
  const fileInputRefs = {
    pdf: useRef<HTMLInputElement>(null),
    screenshot: useRef<HTMLInputElement>(null)
  };
  const [generatedPlan, setGeneratedPlan] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [planTitle, setPlanTitle] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<LessonPlan | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Resource processing states
  const [resourceSummaries, setResourceSummaries] = useState<ResourceSummary[]>([]);
  const [isProcessingResources, setIsProcessingResources] = useState(false);
  const [showResourceSummary, setShowResourceSummary] = useState(false);
  const [resourcesProcessed, setResourcesProcessed] = useState(false);

  const grades = [
  '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
  '6th Grade', '7th Grade', '8th Grade', '9th Grade', '10th Grade',
  '11th Grade', '12th Grade', 'Freshman (College)', 'Sophomore (College)',
  'Junior (College)', 'Senior (College)', 'Graduate', 'Post-Graduate',
  'PhD Level', 'Professional Development'];

  const durations = [
  '30 minutes', '45 minutes', '60 minutes', '90 minutes', '120 minutes'];

  const durationDaysOptions = Array.from({ length: 14 }, (_, i) => i + 1);

  // Load teacher's classes
  const loadClasses = async () => {
    if (!user) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage('15198', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        {
          name: 'teacher_id',
          op: 'Equal',
          value: user.ID
        }]
      });

      if (error) throw error;
      setClasses(data.List || []);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  // Load saved lesson plans
  const loadSavedPlans = async () => {
    if (!user) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage('15206', {
        PageNo: 1,
        PageSize: 50,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        {
          name: 'teacher_id',
          op: 'Equal',
          value: user.ID
        }]
      });

      if (error) throw error;
      setSavedPlans(data.List || []);
    } catch (error) {
      console.error('Error loading saved plans:', error);
    }
  };

  useEffect(() => {
    if (user?.ID) {
      loadClasses();
      loadSavedPlans();
    }
  }, [user?.ID]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (files: FileList | null, type: 'pdfs' | 'screenshots') => {
    if (!files) return;

    const newFiles = Array.from(files);
    setResources((prev) => ({
      ...prev,
      [type]: [...prev[type], ...newFiles]
    }));

    // Reset processed status when new files are added
    setResourcesProcessed(false);
    setShowResourceSummary(false);
  };

  const removeFile = (index: number, type: 'pdfs' | 'screenshots') => {
    setResources((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));

    // Reset processed status when files are removed
    setResourcesProcessed(false);
    setShowResourceSummary(false);
  };

  const handleLinkChange = (index: number, value: string) => {
    setResources((prev) => {
      const newLinks = [...prev.webLinks];
      newLinks[index] = value;
      return { ...prev, webLinks: newLinks };
    });

    // Reset processed status when links are changed
    setResourcesProcessed(false);
    setShowResourceSummary(false);
  };

  const addLink = () => {
    setResources((prev) => ({
      ...prev,
      webLinks: [...prev.webLinks, '']
    }));
  };

  const removeLink = (index: number) => {
    setResources((prev) => ({
      ...prev,
      webLinks: prev.webLinks.filter((_, i) => i !== index)
    }));

    // Reset processed status when links are removed
    setResourcesProcessed(false);
    setShowResourceSummary(false);
  };

  const processResources = async () => {
    if (!canAccessFeature('Resource Processing')) {
      toast({
        title: 'Premium Feature Required',
        description: 'Resource processing requires premium access. Please upgrade to continue.',
        variant: 'destructive'
      });
      return;
    }

    if (!resources.webLinks.some((link) => link.trim()) &&
    resources.pdfs.length === 0 &&
    resources.screenshots.length === 0) {
      toast({
        title: 'No Resources to Process',
        description: 'Please add some resources first.',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessingResources(true);
    const summaries: ResourceSummary[] = [];

    try {
      toast({
        title: 'Processing Resources',
        description: 'Analyzing and summarizing your uploaded resources...'
      });

      // Process web links
      for (const link of resources.webLinks) {
        if (link.trim()) {
          try {
            console.log('Processing web link:', link);
            const result = await ocrService.extractTextFromWebLink(link);

            // Generate summary for the web content
            const summaryPrompt = `
Analyze the following educational content and provide a clear summary for lesson planning:

CONTENT:
${result.substring(0, 2000)}

Please provide:
1. A brief summary (2-3 sentences)
2. 3-5 key points that could be used in a lesson plan
3. The main educational concepts covered

Format your response as:
SUMMARY: [brief summary]
KEY POINTS:
- [point 1]
- [point 2]
- [point 3]
CONCEPTS: [main concepts]
            `;

            const summaryResponse = await geminiService.generateContent(summaryPrompt);
            let summaryText = '';
            if (typeof summaryResponse === 'string') {
              summaryText = summaryResponse;
            } else if (summaryResponse?.text) {
              summaryText = summaryResponse.text;
            }

            // Parse the summary response
            const summaryMatch = summaryText.match(/SUMMARY:\s*(.+?)(?=KEY POINTS:|$)/s);
            const keyPointsMatch = summaryText.match(/KEY POINTS:\s*(.+?)(?=CONCEPTS:|$)/s);

            const summary = summaryMatch?.[1]?.trim() || 'Summary of web content for lesson planning';
            const keyPointsText = keyPointsMatch?.[1]?.trim() || '';
            const keyPoints = keyPointsText.split('\n').filter((point) => point.trim().startsWith('-')).map((point) => point.replace(/^-\s*/, '').trim());

            summaries.push({
              type: 'weblink',
              fileName: link,
              summary,
              keyPoints: keyPoints.length > 0 ? keyPoints : ['Web content analysis available'],
              extractedText: result.substring(0, 1000) + '...',
              url: link
            });
          } catch (error) {
            console.error('Error processing web link:', error);
            toast({
              title: 'Web Link Processing Failed',
              description: `Failed to process ${link}. Continuing with other resources.`,
              variant: 'destructive'
            });
          }
        }
      }

      // Process uploaded files (PDFs and images)
      for (const file of [...resources.pdfs, ...resources.screenshots]) {
        try {
          console.log('Processing file:', file.name);
          const result = await ocrService.processFile(file, formData.subject || 'General');

          // Generate summary for the file content
          const summaryPrompt = `
Analyze the following educational content extracted from a file and provide a clear summary for lesson planning:

FILE: ${file.name}
CONTENT:
${result.extractedText.substring(0, 2000)}

Please provide:
1. A brief summary (2-3 sentences) of what this file contains
2. 3-5 key educational points that could be used in a lesson plan
3. The main topics or concepts covered

Format your response as:
SUMMARY: [brief summary]
KEY POINTS:
- [point 1]
- [point 2]
- [point 3]
CONCEPTS: [main concepts]
          `;

          const summaryResponse = await geminiService.generateContent(summaryPrompt);
          let summaryText = '';
          if (typeof summaryResponse === 'string') {
            summaryText = summaryResponse;
          } else if (summaryResponse?.text) {
            summaryText = summaryResponse.text;
          }

          // Parse the summary response
          const summaryMatch = summaryText.match(/SUMMARY:\s*(.+?)(?=KEY POINTS:|$)/s);
          const keyPointsMatch = summaryText.match(/KEY POINTS:\s*(.+?)(?=CONCEPTS:|$)/s);

          const summary = summaryMatch?.[1]?.trim() || `Content extracted from ${file.name}`;
          const keyPointsText = keyPointsMatch?.[1]?.trim() || '';
          const keyPoints = keyPointsText.split('\n').filter((point) => point.trim().startsWith('-')).map((point) => point.replace(/^-\s*/, '').trim());

          summaries.push({
            type: file.type?.startsWith('image/') ? 'image' : 'pdf',
            fileName: file.name,
            summary,
            keyPoints: keyPoints.length > 0 ? keyPoints : ['File content extracted and analyzed'],
            extractedText: result.extractedText.substring(0, 1000) + '...'
          });
        } catch (error) {
          console.error('Error processing file:', error);
          toast({
            title: 'File Processing Failed',
            description: `Failed to process ${file.name}. Continuing with other resources.`,
            variant: 'destructive'
          });
        }
      }

      setResourceSummaries(summaries);
      setShowResourceSummary(true);
      setResourcesProcessed(true);

      toast({
        title: 'Resource Processing Complete',
        description: `Successfully processed ${summaries.length} resources. Review the summaries before generating your lesson plan.`
      });
    } catch (error) {
      console.error('Error processing resources:', error);
      toast({
        title: 'Processing Failed',
        description: 'Failed to process some resources. You can still generate the lesson plan.',
        variant: 'destructive'
      });
    } finally {
      setIsProcessingResources(false);
    }
  };

  const generateLessonPlan = async () => {
    if (!canAccessFeature('Lesson Plan Generator')) {
      toast({
        title: 'Premium Feature Required',
        description: 'Lesson plan generation requires premium access. Please upgrade to continue.',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.subject || !formData.topic || !formData.grade) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields.',
        variant: 'destructive'
      });
      return;
    }

    // Check if resources need processing
    const hasResources = resources.webLinks.some((link) => link.trim()) ||
    resources.pdfs.length > 0 ||
    resources.screenshots.length > 0;

    if (hasResources && !resourcesProcessed) {
      toast({
        title: 'Process Resources First',
        description: 'Please process your resources first to see what the AI understands from them.',
        variant: 'destructive'
      });
      return;
    }

    setIsGenerating(true);

    try {
      let resourceContext = '';

      if (resourceSummaries.length > 0) {
        toast({
          title: 'Generating Lesson Plan',
          description: 'Creating a comprehensive lesson plan based on your processed resources...'
        });

        // Build resource context from summaries
        resourceContext = resourceSummaries.map((summary) => {
          return `
RESOURCE: ${summary.fileName} (${summary.type})
SUMMARY: ${summary.summary}
KEY POINTS:
${summary.keyPoints.map((point) => `- ${point}`).join('\n')}
CONTENT EXCERPT: ${summary.extractedText}
          `;
        }).join('\n\n');
      } else {
        toast({
          title: 'Generating Lesson Plan',
          description: 'Creating a detailed lesson plan based on your specifications...'
        });
      }

      // Enhanced prompt for lesson plan generation
      const enhancedPrompt = `
Create a comprehensive, detailed lesson plan with the following specifications:

LESSON DETAILS:
- Subject: ${formData.subject}
- Topic: ${formData.topic}
- Grade Level: ${formData.grade}
- Duration: ${formData.duration || `${formData.durationDays} day(s)`}
- Learning Objectives: ${formData.objectives || 'To be developed based on topic'}
- Additional Notes: ${formData.additionalNotes || 'None'}

${resourceContext ? `
RESOURCE MATERIALS PROVIDED:
The following resources have been analyzed and should be integrated into the lesson plan:

${resourceContext}

IMPORTANT: Use the information from these resources to create specific activities, discussions, and assessments. Reference the key points and content excerpts in your lesson plan.
` : ''}

LESSON PLAN REQUIREMENTS:
1. Create a detailed lesson plan that is appropriate for ${formData.grade} students
2. Include clear learning objectives and outcomes
3. Provide step-by-step lesson structure with timing
4. Include engaging activities and discussion questions
5. Suggest assessment methods
6. Provide materials needed
${resourceContext ? '7. Specifically integrate the provided resource materials into lesson activities' : ''}

Format the lesson plan with clear sections and subsections for easy readability.
      `;

      const result = await geminiService.generateContent(enhancedPrompt);
      let planText = '';

      if (typeof result === 'string') {
        planText = result;
      } else if (result?.text) {
        planText = result.text;
      } else if (result?.error) {
        throw new Error(result.error);
      }

      setGeneratedPlan(planText);
      setPlanTitle(`${formData.subject} - ${formData.topic}`);

      let successMessage = 'Your detailed AI-powered lesson plan is ready!';
      if (resourceSummaries.length > 0) {
        successMessage = `Your lesson plan has been generated using ${resourceSummaries.length} processed resource(s). The plan includes specific activities based on your materials.`;
      }

      toast({
        title: 'Lesson Plan Generated!',
        description: successMessage
      });
    } catch (error) {
      console.error('Error generating lesson plan:', error);
      toast({
        title: 'Generation Failed',
        description: error instanceof Error ? error.message : 'Failed to generate lesson plan. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const saveLessonPlan = async () => {
    if (!generatedPlan || !user) {
      toast({
        title: 'Nothing to Save',
        description: 'Please generate a lesson plan first.',
        variant: 'destructive'
      });
      return;
    }

    setIsSaving(true);
    try {
      const lessonPlanData = {
        teacher_id: user.ID,
        class_id: formData.classId ? parseInt(formData.classId) : 0,
        title: planTitle || `${formData.subject} - ${formData.topic}`,
        subject: formData.subject,
        grade_level: formData.grade,
        duration: formData.duration ? parseInt(formData.duration.replace(' minutes', '')) : formData.durationDays * 1440,
        content: generatedPlan,
        objectives: formData.objectives
      };

      const { error } = await window.ezsite.apis.tableCreate('15206', lessonPlanData);

      if (error) throw error;

      toast({
        title: 'Lesson Plan Saved',
        description: 'Your lesson plan has been saved to your library.'
      });

      loadSavedPlans();
    } catch (error) {
      console.error('Error saving lesson plan:', error);
      toast({
        title: 'Save Failed',
        description: 'Failed to save lesson plan. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const downloadLessonPlan = () => {
    if (!generatedPlan) return;

    const element = document.createElement('a');
    const file = new Blob([generatedPlan], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `lesson-plan-${formData.subject}-${formData.topic}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    toast({
      title: 'Download Started',
      description: 'Your lesson plan is being downloaded.'
    });
  };

  const loadSavedPlan = (plan: LessonPlan) => {
    setGeneratedPlan(plan.content || '');
    setPlanTitle(plan.title || '');
    setFormData({
      subject: plan.subject || '',
      topic: plan.title ? plan.title.split(' - ')[1] || '' : '',
      grade: plan.grade_level || '',
      duration: plan.duration && plan.duration < 1440 ? `${plan.duration} minutes` : '',
      durationDays: plan.duration && plan.duration >= 1440 ? Math.floor(plan.duration / 1440) : 1,
      objectives: plan.objectives || '',
      additionalNotes: '',
      classId: plan.class_id ? plan.class_id.toString() : ''
    });
    toast({
      title: 'Lesson Plan Loaded',
      description: 'The lesson plan has been loaded for editing.'
    });
  };

  const viewSavedPlan = (plan: LessonPlan) => {
    setSelectedPlan(plan);
    setIsViewDialogOpen(true);
  };

  const downloadPlan = (plan: LessonPlan) => {
    if (!plan.content) return;

    const element = document.createElement('a');
    const file = new Blob([plan.content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `lesson-plan-${plan.title || 'untitled'}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    toast({
      title: 'Download Started',
      description: 'Your lesson plan is being downloaded.'
    });
  };

  return (
    <FeatureGuard featureName="Lesson Plan Generator" showBanner data-id="idfo9vu9b" data-path="src/components/ai/LessonPlanGenerator.tsx">
      <div className="space-y-6" data-id="4tprm58im" data-path="src/components/ai/LessonPlanGenerator.tsx">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between" data-id="6iarzro7r" data-path="src/components/ai/LessonPlanGenerator.tsx">
          <div data-id="pf87rc8xe" data-path="src/components/ai/LessonPlanGenerator.tsx">
            <h1 className="text-2xl font-bold text-gray-900" data-id="5vqq903zx" data-path="src/components/ai/LessonPlanGenerator.tsx">AI Lesson Plan Generator</h1>
            <p className="text-gray-600" data-id="cxlpjgkkp" data-path="src/components/ai/LessonPlanGenerator.tsx">Create comprehensive, detailed lesson plans with AI assistance</p>
          </div>
          <Badge variant="secondary" className="bg-purple-100 text-purple-700" data-id="6o7gzoi9m" data-path="src/components/ai/LessonPlanGenerator.tsx">
            <Sparkles className="h-3 w-3 mr-1" data-id="griuzoa7m" data-path="src/components/ai/LessonPlanGenerator.tsx" />
            Enhanced with Resource Processing
          </Badge>
        </motion.div>

        <Tabs defaultValue="generate" className="w-full" data-id="b1y7poe0s" data-path="src/components/ai/LessonPlanGenerator.tsx">
          <TabsList data-id="4b64g3d8o" data-path="src/components/ai/LessonPlanGenerator.tsx">
            <TabsTrigger value="generate" data-id="8r7a0y320" data-path="src/components/ai/LessonPlanGenerator.tsx">Generate New Plan</TabsTrigger>
            <TabsTrigger value="saved" data-id="f9as6pknb" data-path="src/components/ai/LessonPlanGenerator.tsx">Saved Plans ({savedPlans.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="generate" className="space-y-6" data-id="pb6l2t083" data-path="src/components/ai/LessonPlanGenerator.tsx">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="agjpxdhxt" data-path="src/components/ai/LessonPlanGenerator.tsx">
              {/* Input Form */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }} data-id="cdogzlrmv" data-path="src/components/ai/LessonPlanGenerator.tsx">
                <Card data-id="wrue0ahf8" data-path="src/components/ai/LessonPlanGenerator.tsx">
                  <CardHeader data-id="23jfroe86" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    <CardTitle className="flex items-center" data-id="w4d0hm0bb" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      <FileText className="h-5 w-5 mr-2" data-id="mwabqn0f5" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                      Lesson Details
                    </CardTitle>
                    <CardDescription data-id="dr9n6elfl" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      Provide information about your lesson to generate a detailed, customized plan
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4" data-id="ti6lmz8js" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="g7wth0reb" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      <div data-id="y8371jvzv" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <Label htmlFor="subject" data-id="sn1mkez1s" data-path="src/components/ai/LessonPlanGenerator.tsx">Subject *</Label>
                        <Input
                          id="subject"
                          placeholder="e.g., Mathematics, Science, History, etc."
                          value={formData.subject}
                          onChange={(e) => handleInputChange('subject', e.target.value)} data-id="3lk1lpdgt" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                      </div>
                      
                      <div data-id="urp6yodwl" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <Label htmlFor="grade" data-id="32iai0kyh" data-path="src/components/ai/LessonPlanGenerator.tsx">Grade Level *</Label>
                        <Select onValueChange={(value) => handleInputChange('grade', value)} data-id="0yhhyt74p" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <SelectTrigger data-id="b1oo6imuk" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <SelectValue placeholder="Select grade" data-id="4lf2vns3m" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                          </SelectTrigger>
                          <SelectContent data-id="xapc2xqvy" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            {grades.map((grade) =>
                            <SelectItem key={grade} value={grade} data-id="d6s5wzxis" data-path="src/components/ai/LessonPlanGenerator.tsx">{grade}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div data-id="kcb2glbib" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      <Label htmlFor="topic" data-id="a7mcnztor" data-path="src/components/ai/LessonPlanGenerator.tsx">Topic/Chapter *</Label>
                      <Input
                        id="topic"
                        placeholder="e.g., Photosynthesis, World War II, Algebra"
                        value={formData.topic}
                        onChange={(e) => handleInputChange('topic', e.target.value)} data-id="kapgr2z0k" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="kbtez2phs" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      <div data-id="6nfpfeoab" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <Label htmlFor="duration" data-id="ym7bva5fd" data-path="src/components/ai/LessonPlanGenerator.tsx">Single Lesson Duration</Label>
                        <Select onValueChange={(value) => handleInputChange('duration', value)} data-id="anqp8nzoe" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <SelectTrigger data-id="jc8x5zau1" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <SelectValue placeholder="Select duration" data-id="84ro57nm3" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                          </SelectTrigger>
                          <SelectContent data-id="6xqdcb4df" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            {durations.map((duration) =>
                            <SelectItem key={duration} value={duration} data-id="s1to1u650" data-path="src/components/ai/LessonPlanGenerator.tsx">{duration}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div data-id="bnq4gbwva" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <Label htmlFor="durationDays" data-id="g4bafw6uo" data-path="src/components/ai/LessonPlanGenerator.tsx">Plan Duration (Days) *</Label>
                        <Select onValueChange={(value) => handleInputChange('durationDays', value)} data-id="s4r95nrd4" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <SelectTrigger data-id="x66rn425a" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <SelectValue placeholder="Select days" data-id="j3t5pwnjx" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                          </SelectTrigger>
                          <SelectContent data-id="on2vmej93" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            {durationDaysOptions.map((days) =>
                            <SelectItem key={days} value={days.toString()} data-id="2iu7fcx2y" data-path="src/components/ai/LessonPlanGenerator.tsx">{days} {days === 1 ? 'day' : 'days'}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div data-id="iaijf179t" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <Label htmlFor="classId" data-id="1ua666270" data-path="src/components/ai/LessonPlanGenerator.tsx">Assign to Class (Optional)</Label>
                        <Select onValueChange={(value) => handleInputChange('classId', value)} data-id="i0tqpo9kp" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <SelectTrigger data-id="j7guqr0sh" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <SelectValue placeholder="Select class" data-id="1sfre68x8" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                          </SelectTrigger>
                          <SelectContent data-id="jyqeef6k6" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            {classes.filter((cls) => cls && cls.ID).map((cls) =>
                            <SelectItem key={cls.ID} value={cls.ID.toString()} data-id="4y358pztp" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                {cls.class_name || 'Unnamed Class'} - {cls.subject || 'No Subject'}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div data-id="x5v2k2ii8" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      <Label htmlFor="objectives" data-id="qgmpdmo0q" data-path="src/components/ai/LessonPlanGenerator.tsx">Learning Objectives</Label>
                      <Textarea
                        id="objectives"
                        placeholder="What should students learn from this lesson? (optional)"
                        value={formData.objectives}
                        onChange={(e) => handleInputChange('objectives', e.target.value)}
                        rows={3} data-id="iam6hgjn0" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                    </div>

                    <div data-id="zhygktdkb" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      <Label htmlFor="additionalNotes" data-id="q8mcxlj7i" data-path="src/components/ai/LessonPlanGenerator.tsx">Additional Notes</Label>
                      <Textarea
                        id="additionalNotes"
                        placeholder="Any specific requirements, activities, or notes (optional)"
                        value={formData.additionalNotes}
                        onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                        rows={3} data-id="nieezf7w8" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                    </div>

                    {/* Enhanced Resource Section */}
                    <div className="space-y-4 border-t pt-4" data-id="9yfytgscb" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      <div className="flex items-center space-x-2" data-id="w13z4wa93" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <Label className="text-base font-semibold" data-id="lz59vwdnf" data-path="src/components/ai/LessonPlanGenerator.tsx">Resources</Label>
                        <Badge variant="outline" className="text-xs" data-id="kkqica6gr" data-path="src/components/ai/LessonPlanGenerator.tsx">Resources will be analyzed and summarized</Badge>
                      </div>
                      
                      {/* PDF Upload */}
                      <div data-id="4kvyggrdy" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <Label data-id="1o4xncpzc" data-path="src/components/ai/LessonPlanGenerator.tsx">PDF Documents</Label>
                        <div className="mt-2" data-id="bmaaik8ps" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <input
                            ref={fileInputRefs.pdf}
                            type="file"
                            accept=".pdf"
                            multiple
                            className="hidden"
                            onChange={(e) => handleFileUpload(e.target.files, 'pdfs')} data-id="37dlpoj2d" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRefs.pdf.current?.click()}
                            className="w-full" data-id="d0ci0kjtd" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <Upload className="h-4 w-4 mr-2" data-id="nt95nf0cx" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                            Upload PDFs
                          </Button>
                          {resources.pdfs.length > 0 &&
                          <div className="mt-2 space-y-1" data-id="gcrtiz6sj" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              {resources.pdfs.map((file, index) =>
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded" data-id="d7d35ii76" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                  <div className="flex items-center space-x-2" data-id="ssijhd83g" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                    <FileIcon className="h-4 w-4 text-red-600" data-id="7utt5bnm2" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                    <span className="text-sm truncate" data-id="dlir9o0zr" data-path="src/components/ai/LessonPlanGenerator.tsx">{file.name}</span>
                                  </div>
                                  <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFile(index, 'pdfs')} data-id="gowysvh6u" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                    <X className="h-3 w-3" data-id="1yrfgfq31" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                  </Button>
                                </div>
                            )}
                            </div>
                          }
                        </div>
                      </div>

                      {/* Screenshot Upload */}
                      <div data-id="ilu8x973u" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <Label data-id="1v4qmr6sd" data-path="src/components/ai/LessonPlanGenerator.tsx">Screenshots/Images</Label>
                        <div className="mt-2" data-id="buziplyr0" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <input
                            ref={fileInputRefs.screenshot}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => handleFileUpload(e.target.files, 'screenshots')} data-id="6rzbx01zl" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRefs.screenshot.current?.click()}
                            className="w-full" data-id="n00dvsat0" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <Upload className="h-4 w-4 mr-2" data-id="our7x1f7g" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                            Upload Images
                          </Button>
                          {resources.screenshots.length > 0 &&
                          <div className="mt-2 space-y-1" data-id="qz57ont8f" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              {resources.screenshots.map((file, index) =>
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded" data-id="5p1ly8bih" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                  <div className="flex items-center space-x-2" data-id="3ns0j8hve" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                    <ImageIcon className="h-4 w-4 text-green-600" data-id="gpmjgtpvs" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                    <span className="text-sm truncate" data-id="fakkdvwnw" data-path="src/components/ai/LessonPlanGenerator.tsx">{file.name}</span>
                                  </div>
                                  <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFile(index, 'screenshots')} data-id="mydywdsbz" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                    <X className="h-3 w-3" data-id="rsmxv8ux9" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                  </Button>
                                </div>
                            )}
                            </div>
                          }
                        </div>
                      </div>

                      {/* Web Links */}
                      <div data-id="zjl6bwzlf" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <Label data-id="3b0tm5zgn" data-path="src/components/ai/LessonPlanGenerator.tsx">Web Links</Label>
                        <div className="mt-2 space-y-2" data-id="q4uzqwya0" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          {resources.webLinks.map((link, index) =>
                          <div key={index} className="flex items-center space-x-2" data-id="8wbwy7jeh" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              <Input
                              placeholder="https://example.com"
                              value={link}
                              onChange={(e) => handleLinkChange(index, e.target.value)} data-id="4x69wq8xz" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                              {resources.webLinks.length > 1 &&
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLink(index)} data-id="ejxv5ksdm" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                  <X className="h-3 w-3" data-id="u6wcgr6a7" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                </Button>
                            }
                            </div>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={addLink}
                            className="w-full" data-id="vpmo7r6v0" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <Plus className="h-3 w-3 mr-1" data-id="2jyinbktb" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                            Add Web Link
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Resource Processing Section */}
                    {(resources.webLinks.some((link) => link.trim()) ||
                    resources.pdfs.length > 0 ||
                    resources.screenshots.length > 0) &&
                    <div className="space-y-4 border-t pt-4" data-id="bbhhympuk" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <div className="flex items-center justify-between" data-id="f8k4vpejt" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <Label className="text-base font-semibold" data-id="4ibrp73km" data-path="src/components/ai/LessonPlanGenerator.tsx">Resource Processing</Label>
                          <Badge variant="outline" className={`text-xs ${resourcesProcessed ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`} data-id="h9vyh1ru9" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            {resourcesProcessed ?
                          <>
                                <CheckCircle2 className="h-3 w-3 mr-1" data-id="mnr1eip52" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                Processed
                              </> :

                          <>
                                <AlertCircle className="h-3 w-3 mr-1" data-id="lk2z2o2nf" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                Not Processed
                              </>
                          }
                          </Badge>
                        </div>
                        
                        <div className="space-y-2" data-id="2xvs7fzqu" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <Button
                          type="button"
                          onClick={processResources}
                          disabled={isProcessingResources}
                          className="w-full"
                          variant="outline" data-id="botu405zz" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            {isProcessingResources ?
                          <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="es8102qyd" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                Processing Resources...
                              </> :

                          <>
                                <Search className="h-4 w-4 mr-2" data-id="m9q4u0uyd" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                Process & Summarize Resources
                              </>
                          }
                          </Button>
                          
                          <p className="text-xs text-gray-600 text-center" data-id="b7ae1jhtr" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            Process your resources to see what the AI understands before generating the lesson plan
                          </p>
                        </div>

                        {/* Resource Summary Results */}
                        {showResourceSummary && resourceSummaries.length > 0 &&
                      <div className="space-y-3 mt-4" data-id="v8v6nszyp" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <div className="flex items-center justify-between" data-id="a4hxrjenu" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              <h4 className="text-sm font-semibold text-gray-800" data-id="cwkl4v0hh" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                Resource Summaries
                              </h4>
                              <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowResourceSummary(false)} data-id="he5963ssp" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                <ChevronUp className="h-4 w-4" data-id="lv9wxgsg7" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                              </Button>
                            </div>
                            
                            <div className="space-y-3 max-h-96 overflow-y-auto" data-id="bd9ul29ud" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              {resourceSummaries.map((summary, index) =>
                          <div key={index} className="bg-gray-50 p-3 rounded-lg border" data-id="pip46ns78" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                  <div className="flex items-center space-x-2 mb-2" data-id="n095jct13" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                    {summary.type === 'pdf' && <FileIcon className="h-4 w-4 text-red-600" data-id="pu9apl7f2" data-path="src/components/ai/LessonPlanGenerator.tsx" />}
                                    {summary.type === 'image' && <ImageIcon className="h-4 w-4 text-green-600" data-id="qqjzxejfe" data-path="src/components/ai/LessonPlanGenerator.tsx" />}
                                    {summary.type === 'weblink' && <ExternalLink className="h-4 w-4 text-blue-600" data-id="klljjbzkj" data-path="src/components/ai/LessonPlanGenerator.tsx" />}
                                    <span className="text-sm font-medium" data-id="iqenxkth8" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                      {summary.type === 'pdf' && 'PDF Document'}
                                      {summary.type === 'image' && 'Image File'}
                                      {summary.type === 'weblink' && 'Web Resource'}
                                    </span>
                                  </div>
                                  
                                  <p className="text-xs text-gray-600 mb-2 break-all" data-id="dibvdicqa" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                    {summary.fileName}
                                  </p>
                                  
                                  <div className="space-y-2" data-id="dazghqd0k" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                    <div data-id="ulvv1calp" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                      <span className="text-xs font-medium text-gray-700" data-id="5p5q0v1qo" data-path="src/components/ai/LessonPlanGenerator.tsx">Summary:</span>
                                      <p className="text-xs text-gray-600" data-id="isei7ia5q" data-path="src/components/ai/LessonPlanGenerator.tsx">{summary.summary}</p>
                                    </div>
                                    
                                    {summary.keyPoints.length > 0 &&
                              <div data-id="brs796p3j" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                        <span className="text-xs font-medium text-gray-700" data-id="1272727yh" data-path="src/components/ai/LessonPlanGenerator.tsx">Key Points:</span>
                                        <ul className="text-xs text-gray-600 list-disc list-inside ml-2" data-id="6yezpx2s7" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                          {summary.keyPoints.map((point, pointIndex) =>
                                  <li key={pointIndex} data-id="dasiqukdo" data-path="src/components/ai/LessonPlanGenerator.tsx">{point}</li>
                                  )}
                                        </ul>
                                      </div>
                              }
                                  </div>
                                </div>
                          )}
                            </div>
                            
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200" data-id="1kkhkfd50" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              <p className="text-sm text-green-800" data-id="0xuvr7kyw" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                <strong data-id="2frrx55xl" data-path="src/components/ai/LessonPlanGenerator.tsx">Resources Processed:</strong> The AI has analyzed {resourceSummaries.length} resource(s). 
                                These summaries show what content will be integrated into your lesson plan. 
                                You can now generate your lesson plan with confidence that your resources are being utilized.
                              </p>
                            </div>
                          </div>
                      }
                      </div>
                    }

                    <Button
                      onClick={generateLessonPlan}
                      disabled={isGenerating}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" data-id="089cagivq" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      {isGenerating ?
                      <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="q14lcwyb2" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                          Creating Detailed Lesson Plan...
                        </> :
                      <>
                          <Sparkles className="mr-2 h-4 w-4" data-id="rgwo7lkjg" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                          Generate Detailed Lesson Plan
                        </>
                      }
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Generated Plan */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }} data-id="tc0hqla9w" data-path="src/components/ai/LessonPlanGenerator.tsx">
                <Card className="h-full" data-id="bs2r4ujqu" data-path="src/components/ai/LessonPlanGenerator.tsx">
                  <CardHeader data-id="c6cbpd1ef" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    <div className="flex items-center justify-between" data-id="dkfdmkh1e" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      <div data-id="92oqtjmxz" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <CardTitle data-id="l089qsph8" data-path="src/components/ai/LessonPlanGenerator.tsx">Generated Lesson Plan</CardTitle>
                        <CardDescription data-id="jhfye6m4p" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          {generatedPlan ? 'Your comprehensive AI-generated lesson plan' : 'Generated plan will appear here'}
                        </CardDescription>
                      </div>
                      {generatedPlan &&
                      <div className="flex space-x-2" data-id="d1scjy59i" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <Button
                          size="sm"
                          variant="outline"
                          onClick={saveLessonPlan}
                          disabled={isSaving} data-id="kyj7kq84p" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            {isSaving ?
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" data-id="063nvjfjl" data-path="src/components/ai/LessonPlanGenerator.tsx" /> :
                          <Save className="h-4 w-4 mr-1" data-id="qw2ui2zqz" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                          }
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={downloadLessonPlan} data-id="q8be4yhk4" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <Download className="h-4 w-4 mr-1" data-id="0i4ric4et" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                            Download
                          </Button>
                        </div>
                      }
                    </div>
                  </CardHeader>
                  <CardContent data-id="0fk7owvax" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    {generatedPlan ?
                    <div className="bg-gray-50 rounded-lg p-4 h-96 overflow-y-auto" data-id="tycktjp8a" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <div className="prose prose-sm max-w-none" data-id="7iklvpulr" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <ReactMarkdown className="whitespace-pre-wrap text-sm" data-id="rzc5hfasu" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            {generatedPlan}
                          </ReactMarkdown>
                        </div>
                      </div> :
                    <div className="flex items-center justify-center h-96 text-gray-500" data-id="70oipb2xv" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <div className="text-center" data-id="hax41rhzl" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          {isGenerating ?
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex flex-col items-center" data-id="g548ha5p2" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }} data-id="fuvxkqwok" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                <Sparkles className="h-12 w-12 mx-auto mb-4 text-purple-500" data-id="a9gbp65pu" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                              </motion.div>
                              <motion.p
                            animate={{ opacity: [1, 0.5, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="text-lg font-medium text-purple-600" data-id="n2mio5i6i" data-path="src/components/ai/LessonPlanGenerator.tsx">Creating detailed lesson plan...</motion.p>
                              {resourceSummaries.length > 0 ?
                          <p className="text-sm text-gray-500 mt-2" data-id="xzvvlztmb" data-path="src/components/ai/LessonPlanGenerator.tsx">Integrating {resourceSummaries.length} processed resource(s) into lesson activities</p> :
                          <p className="text-sm text-gray-500 mt-2" data-id="5fmdmw4qm" data-path="src/components/ai/LessonPlanGenerator.tsx">Generating comprehensive lesson plan with discussion questions</p>
                          }
                            </motion.div> :
                        <div data-id="56hjl15g2" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" data-id="ocar1prow" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                              <p data-id="ngays3vqz" data-path="src/components/ai/LessonPlanGenerator.tsx">Fill in the lesson details and click "Generate Detailed Lesson Plan" to get started</p>
                              <p className="text-sm text-gray-400 mt-2" data-id="e09psc2jb" data-path="src/components/ai/LessonPlanGenerator.tsx">Add resources and process them for more comprehensive lesson plans</p>
                            </div>
                        }
                        </div>
                      </div>
                    }
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </TabsContent>

          <TabsContent value="saved" className="space-y-6" data-id="7iwyzrqqz" data-path="src/components/ai/LessonPlanGenerator.tsx">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="yw7g63lpe" data-path="src/components/ai/LessonPlanGenerator.tsx">
              {savedPlans.length === 0 ?
              <div className="col-span-full text-center py-12" data-id="7evq3oumf" data-path="src/components/ai/LessonPlanGenerator.tsx">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" data-id="1kclxairf" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                  <p className="text-gray-500" data-id="6o5gne8jo" data-path="src/components/ai/LessonPlanGenerator.tsx">No saved lesson plans yet</p>
                  <p className="text-sm text-gray-400" data-id="b9oxwj69e" data-path="src/components/ai/LessonPlanGenerator.tsx">Generate and save your first lesson plan to see it here</p>
                </div> :
              savedPlans.map((plan, index) =>
              <motion.div
                key={plan.ID || index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }} data-id="cu0dynbcj" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    <Card className="hover:shadow-lg transition-shadow" data-id="v25xmdwdn" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      <CardHeader data-id="fb8a4rtuj" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <CardTitle className="text-lg" data-id="1fhr7pyxa" data-path="src/components/ai/LessonPlanGenerator.tsx">{plan.title || 'Untitled Plan'}</CardTitle>
                        <CardDescription data-id="tc6cvagfo" data-path="src/components/ai/LessonPlanGenerator.tsx">{plan.subject || 'No Subject'} - {plan.grade_level || 'No Grade'}</CardDescription>
                      </CardHeader>
                      <CardContent data-id="6o5ekwe91" data-path="src/components/ai/LessonPlanGenerator.tsx">
                        <div className="space-y-2" data-id="gkro0rtkm" data-path="src/components/ai/LessonPlanGenerator.tsx">
                          <div className="flex items-center text-sm text-gray-600" data-id="vj12nfykk" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <Clock className="h-4 w-4 mr-2" data-id="b70a23qjn" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                            {plan.duration && plan.duration < 1440 ? `${plan.duration} minutes` : plan.duration >= 1440 ? `${Math.floor(plan.duration / 1440)} days` : 'Duration not set'}
                          </div>
                          {plan.objectives &&
                      <div className="text-sm text-gray-600" data-id="4vmsu6q4m" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              <strong data-id="1aibv10f2" data-path="src/components/ai/LessonPlanGenerator.tsx">Objectives:</strong> {(plan.objectives || '').substring(0, 100)}...
                            </div>
                      }
                          <div className="flex flex-col gap-2 mt-4" data-id="deu8gzwo3" data-path="src/components/ai/LessonPlanGenerator.tsx">
                            <div className="flex items-center justify-between" data-id="t6fid4341" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              <Badge variant="secondary" data-id="pfq4rrsso" data-path="src/components/ai/LessonPlanGenerator.tsx">{plan.subject || 'No Subject'}</Badge>
                            </div>
                            <div className="flex gap-2" data-id="iw75nwrad" data-path="src/components/ai/LessonPlanGenerator.tsx">
                              <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewSavedPlan(plan)}
                            className="flex-1" data-id="7rjfeyd28" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                <Eye className="h-4 w-4 mr-1" data-id="2ilfcmvcl" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                View
                              </Button>
                              <Button
                            size="sm"
                            variant="outline"
                            onClick={() => loadSavedPlan(plan)}
                            className="flex-1" data-id="vnuyx7e5i" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                <Edit className="h-4 w-4 mr-1" data-id="30nazq6iz" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                                Edit
                              </Button>
                              <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadPlan(plan)} data-id="hr994lz7h" data-path="src/components/ai/LessonPlanGenerator.tsx">
                                <Download className="h-4 w-4" data-id="r1mcwivjo" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
              )
              }
            </motion.div>
          </TabsContent>
        </Tabs>

        {/* View Lesson Plan Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen} data-id="7hh37gzk5" data-path="src/components/ai/LessonPlanGenerator.tsx">
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden" data-id="wcspms85w" data-path="src/components/ai/LessonPlanGenerator.tsx">
            <DialogHeader data-id="znf5g65js" data-path="src/components/ai/LessonPlanGenerator.tsx">
              <DialogTitle className="flex items-center justify-between" data-id="9obrflvrx" data-path="src/components/ai/LessonPlanGenerator.tsx">
                <span data-id="ihbudiwjy" data-path="src/components/ai/LessonPlanGenerator.tsx">{selectedPlan?.title || 'Untitled Plan'}</span>
                <div className="flex gap-2" data-id="299ncyxeq" data-path="src/components/ai/LessonPlanGenerator.tsx">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => selectedPlan && downloadPlan(selectedPlan)} data-id="vf8tmrq5i" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    <Download className="h-4 w-4 mr-1" data-id="5ulsmm9ya" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                    Download
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (selectedPlan) {
                        loadSavedPlan(selectedPlan);
                        setIsViewDialogOpen(false);
                      }
                    }} data-id="zmawl5ngo" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    <Edit className="h-4 w-4 mr-1" data-id="r72gcjxhj" data-path="src/components/ai/LessonPlanGenerator.tsx" />
                    Edit
                  </Button>
                </div>
              </DialogTitle>
              <DialogDescription data-id="blc9iwc4o" data-path="src/components/ai/LessonPlanGenerator.tsx">
                {selectedPlan?.subject || 'No Subject'} - {selectedPlan?.grade_level || 'No Grade'}
                {selectedPlan?.duration &&
                <span className="ml-2" data-id="05tptv12j" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    • {selectedPlan.duration < 1440 ? `${selectedPlan.duration} minutes` : `${Math.floor(selectedPlan.duration / 1440)} days`}
                  </span>
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4" data-id="2y3jjjrn4" data-path="src/components/ai/LessonPlanGenerator.tsx">
              {selectedPlan?.objectives &&
              <div data-id="t2tzr8yhh" data-path="src/components/ai/LessonPlanGenerator.tsx">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2" data-id="7b639iswo" data-path="src/components/ai/LessonPlanGenerator.tsx">Learning Objectives:</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg" data-id="kd780xrlz" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    {selectedPlan.objectives}
                  </p>
                </div>
              }
              
              <div data-id="u7m4otldk" data-path="src/components/ai/LessonPlanGenerator.tsx">
                <h4 className="font-semibold text-sm text-gray-700 mb-2" data-id="557n7gi4z" data-path="src/components/ai/LessonPlanGenerator.tsx">Lesson Plan Content:</h4>
                <div className="bg-gray-50 rounded-lg p-4 h-[400px] overflow-y-auto" data-id="puswfo4kf" data-path="src/components/ai/LessonPlanGenerator.tsx">
                  <div className="prose prose-sm max-w-none" data-id="bgbzvcqmz" data-path="src/components/ai/LessonPlanGenerator.tsx">
                    <ReactMarkdown className="whitespace-pre-wrap text-sm" data-id="m11ei3s23" data-path="src/components/ai/LessonPlanGenerator.tsx">
                      {selectedPlan?.content || 'No content available'}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </FeatureGuard>);

};

export default LessonPlanGenerator;